import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { GapWarning } from '../../components/ui/GapWarning.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { ApiError, api } from '../../services/api.js';
import { type LateContext, LateExtensionForm } from './LateExtensionForm.js';

/**
 * 325 (#477, US4): staff extend a customer's rental, for a customer who phones in. The platform says
 * what the rental may be extended to (`GET /bookings/:id/extend-context`, judged on the customer's
 * own facts), the new window is quoted on the same car, and the extension is created through the
 * staff create endpoint WITH the link to the rental, so it is a linked extension with every check a
 * customer's own gets. The customer is then asked to pay it, as for any booking staff record.
 */

interface ExtendableBooking {
  id: string;
  user_id: string;
  car_id: string;
  location_id: string;
  status: string;
  child_booking_id?: string;
}

interface ExtendContext {
  extendable: boolean;
  reason: string | null;
  earliest_return_at: string | null;
  latest_return_at: string | null;
  car_id: string | null;
  pickup_at: string | null;
  tail_booking_id: string | null;
  /** 421: whether the return time has passed, and whether this caller may record a late extension. */
  late?: LateContext | null;
}

interface QuotePreview {
  outcome: string;
  pricing_template_id?: string;
  currency_symbol?: string;
  total_minor?: number;
}

/** Why a rental cannot be extended, for staff (the reasons `extendEligibility.ts` answers). */
const REASONS: Record<string, string> = {
  unpaid_extension_exists: 'An extension of this rental is already waiting for payment.',
  booking_not_extendable: 'This rental is not active, so it can’t be extended.',
  overdue: 'This rental’s return time has already passed, so it can’t be extended.',
  email_not_verified: 'The customer has not verified their email address yet.',
  identity_not_verified: 'The customer’s identity check has not been approved yet.',
  inside_notice_period: 'It is too close to the return time to extend this rental.',
  allowance_spent: 'This rental has used up its extension allowance.',
  no_window: 'The car is booked straight after this rental, so there is no time to extend into.',
};

const EXTENDABLE = new Set(['confirmed', 'checked_out']);
// 421: a late extension may follow a rental that is still out or has ended.
const LATE_CANDIDATE = new Set(['checked_out', 'checked_in', 'completed']);

export function ExtendRentalCard({ booking }: { booking: ExtendableBooking }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [returnAt, setReturnAt] = useState('');
  const [error, setError] = useState('');

  const lastLeg = !booking.child_booking_id;
  const isTail = EXTENDABLE.has(booking.status) && lastLeg;
  const lateCandidate = LATE_CANDIDATE.has(booking.status) && lastLeg;

  const { data: context, isError: contextFailed } = useQuery<ExtendContext>({
    queryKey: ['booking-extend-context', booking.id],
    queryFn: () => api.get<ExtendContext>(`/bookings/${booking.id}/extend-context`),
    enabled: (isTail || lateCandidate) && open,
  });

  const returnIso = returnAt ? new Date(returnAt).toISOString() : '';
  const { data: quote } = useQuery<QuotePreview>({
    queryKey: ['booking-extend-quote', booking.id, returnIso],
    queryFn: () =>
      api.post<QuotePreview>('/pricing/quote', {
        car_id: booking.car_id,
        pickup_at: context?.pickup_at,
        return_at: returnIso,
      }),
    enabled: Boolean(context?.extendable && context.pickup_at && returnIso),
  });

  const create = useSaveMutation<Record<string, unknown>, { id: string }>({
    resource: 'booking',
    mutationFn: (body) => api.post<{ id: string }>('/bookings/admin-create', body),
    onSaved: (res) => {
      setError('');
      navigate(`/bookings/${res.id}`);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Failed to extend.');
    },
  });

  if (!isTail && !lateCandidate) return null;
  const late = context?.late?.return_passed ? context.late : null;

  const priced = quote?.outcome === 'priced' && quote.pricing_template_id;

  function onCreate() {
    if (!context?.pickup_at || !context.tail_booking_id || !returnIso) {
      setError('Choose the new return time.');
      return;
    }
    if (!priced) {
      setError('No price could be resolved for this car and window.');
      return;
    }
    void create.save({
      user_id: booking.user_id,
      car_id: booking.car_id,
      location_id: booking.location_id,
      pickup_at: context.pickup_at,
      return_at: returnIso,
      pricing_template_id: quote?.pricing_template_id,
      parent_booking_id: context.tail_booking_id,
    });
  }

  return (
    <Card>
      {/* 421: the late form carries its own heading, "Extend rental (late)". */}
      {!late?.allowed && <h2 className="text-sm font-semibold text-ink mb-2">Extend rental</h2>}
      {!open ? (
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Extend rental
        </Button>
      ) : contextFailed ? (
        <p className="text-sm text-semantic-danger-text">
          Whether this rental can be extended could not be loaded.
        </p>
      ) : !context ? (
        <p className="text-sm text-steel">Checking this rental…</p>
      ) : late && !late.allowed ? (
        <p data-testid="late-extension-refused" className="text-sm text-steel">
          The return time has passed. Only staff allowed to change past bookings can extend it now.
        </p>
      ) : late ? (
        <LateExtensionForm late={late} onCancel={() => setOpen(false)} />
      ) : !context.extendable ? (
        <p data-testid="extend-rental-refusal" className="text-sm text-semantic-danger-text">
          {REASONS[context.reason ?? ''] ?? 'This rental can’t be extended right now.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-steel">
            Same car, starting when the rental ends. The customer is asked to pay the extension.
          </p>
          <FormField label="Extend until" name="extend-return-at">
            <Input
              id="extend-return-at"
              type="datetime-local"
              value={returnAt}
              onChange={(e) => setReturnAt(e.target.value)}
            />
          </FormField>
          {priced && (
            <p className="text-sm text-ink">
              Extension price:{' '}
              <span data-testid="extend-rental-price" className="font-semibold">
                {quote?.currency_symbol ?? 'RM'} {((quote?.total_minor ?? 0) / 100).toFixed(2)}
              </span>
            </p>
          )}
          {error && (
            <p data-testid="extend-rental-error" className="text-sm text-semantic-danger-text">
              {error}
            </p>
          )}
          {/* 386 (#1278, FR-011): the gap after the extended return — a warning only. */}
          <GapWarning
            carId={booking.car_id}
            pickupAt={context?.pickup_at ?? null}
            returnAt={returnIso ?? null}
            bookingId={booking.id}
          />
          <div>
            <Button onClick={onCreate} disabled={create.isPending}>
              Create extension
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
